import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      insomnia: 'Insomnio',
      anxiety: 'Ansiedad',
      technology: 'Tecnología',
      pricing: 'Precios',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      dashboard: 'Mis Audios',
      logout: 'Cerrar Sesión',
      profile: 'Perfil'
    },
    // Home page
    home: {
      title: 'BIOSOUNDS',
      subtitle: 'Neuroacústica Aplicada - Música y Frecuencias Binaurales',
      description: 'BIOSOUNDS es una plataforma innovadora de neuroacústica aplicada que utiliza música y frecuencias binaurales 8D para inducir estados cerebrales específicos mediante el principio de Frequency Following Response (FFR).',
      cta: 'Evalúa tu Salud Nerviosa',
      ctaDescription: 'Obtén un puntaje personalizado de equilibrio autónomo en menos de 5 minutos',
      benefits: {
        title: 'Beneficios Científicamente Validados',
        items: [
          { title: 'Sueño Profundo', desc: 'Inducción de ondas Delta para sueño restaurador', stat: '+48% calidad' },
          { title: 'Reducción de Ansiedad', desc: 'Modulación del eje HPA y tono vagal', stat: '-35% cortisol' },
          { title: 'Mejora HRV', desc: 'Variabilidad de frecuencia cardíaca optimizada', stat: '+18% HRV' },
          { title: 'Anti-inflamatorio', desc: 'Inhibición de citoquinas proinflamatorias', stat: '-78% IL-6' }
        ]
      },
      science: {
        title: 'Respaldado por más de 50 estudios científicos (2020-2025)',
        items: [
          'Meta-análisis de Baseanu 2024: 35% reducción de cortisol',
          'Dabiri 2022: 48% reducción de latencia de sueño',
          'Yang 2025: 18% mejora en HRV',
          'Vanneste 2017: 78% reducción de marcadores inflamatorios'
        ]
      }
    },
    // Insomnia page
    insomnia: {
      title: 'Acaba con el Insomnio Crónico',
      subtitle: 'Restaura tu Ciclo Circadiano con Ondas Delta Validadas',
      description: 'El audio BIOSOUNDS Ciclo Circadiano (Delta) de 20 minutos aplica frecuencias binaurales para inducir ondas Delta (0.5–4 Hz) y Theta (4–8 Hz), facilitando la transición rápida al sueño profundo NREM.',
      benefits: [
        'Inducción precisa de ondas Delta para sueño profundo restaurador',
        'Reparación física y psíquica nocturna',
        'Eliminación de despertares nocturnos (hasta 48% menos)',
        'Restauración del ritmo circadiano alterado',
        'Reducción de inflamación nocturna'
      ],
      results: {
        title: 'Resultados Esperados',
        items: [
          { week: 'Semana 1', desc: 'Sueño más continuo, reducción inicial de interrupciones' },
          { week: 'Semana 2-4', desc: 'Mayor profundidad del sueño, mejora significativa en HRV' },
          { week: 'Mes 2+', desc: 'Energía diurna sostenida, equilibrio autónomo estable' }
        ]
      },
      cta: 'Prueba el Audio Delta'
    },
    // Anxiety page
    anxiety: {
      title: 'Controla la Ansiedad y el Cortisol',
      subtitle: 'Recupera tu Calma Natural con Ondas Theta Científicamente Validadas',
      description: 'El audio BIOSOUNDS Control Cortisol (Theta) de 23 minutos utiliza ondas Theta (4–8 Hz) para normalizar el metabolismo del estrés, estabilizar el eje HPA y reducir la respuesta emocional hiperactiva.',
      benefits: [
        'Baja drástica de cortisol circulante (reducción media del 35%)',
        'Disminución de ansiedad emocional y conductual',
        'Mayor claridad mental y estabilidad emocional (+45%)',
        'Apoyo metabólico natural a la pérdida de peso',
        'Aumento del tono vagal y HRV (+18%)'
      ],
      results: {
        title: 'Resultados Esperados',
        items: [
          { week: 'Semana 1', desc: 'Sensación de calma inmediata y reducción de picos de ansiedad' },
          { week: 'Semana 2-4', desc: 'Menos episodios de ansiedad generalizada' },
          { week: 'Mes 2+', desc: 'Equilibrio emocional sostenido, energía sin agotamiento' }
        ]
      },
      cta: 'Prueba el Audio Theta'
    },
    // Technology page
    technology: {
      title: 'La Tecnología detrás de BIOSOUNDS',
      subtitle: 'Avances en Neurobiomodulación Sonora y Neuromodulación Auditiva',
      description: 'BIOSOUNDS integra principios de biofísica, neurociencia y bioeléctrica para ofrecer una neuromodulación sonora no invasiva de vanguardia.',
      mechanisms: {
        title: 'Mecanismos Científicos Principales',
        items: [
          { title: 'Frequency Following Response (FFR)', desc: 'Sincronización de oscilaciones neuronales con frecuencias externas' },
          { title: 'Bio-resonancia Celular', desc: 'Restablece vibraciones saludables en células estresadas' },
          { title: 'Modulación del Eje HPA', desc: 'Aumento de HRV en 18% y reducción de inflamación' },
          { title: 'Estimulación del Nervio Vago', desc: 'Vías auditivas activan el reflejo parasimpático' }
        ]
      },
      evidence: {
        title: 'Evidencia Científica (2020-2025)',
        items: [
          'Reducción de ansiedad: 26-35% en escalas estandarizadas (Baseanu 2024)',
          'Mejora del sueño: latencia reducida 48%, calidad +31% (Dabiri 2022)',
          'Aumento de HRV: +18% tras sesiones regulares (Yang 2025)',
          'Reducción inflamatoria: inhibición de citoquinas (Vanneste 2017)',
          '0 eventos adversos graves reportados en la literatura'
        ]
      }
    },
    // Pricing page
    pricing: {
      title: 'Elige tu Plan',
      subtitle: 'Acceso ilimitado a todos los audios de neuromodulación',
      monthly: {
        name: 'Plan Mensual',
        price: '2,99€',
        period: '/mes',
        features: ['Acceso a todos los audios', 'Streaming ilimitado', 'Nuevos contenidos mensuales', 'Soporte por email']
      },
      annual: {
        name: 'Plan Anual',
        price: '25,12€',
        period: '/año',
        discount: 'Ahorra 30%',
        features: ['Todo lo del plan mensual', 'Equivale a 2,09€/mes', 'Acceso prioritario a novedades', 'Soporte prioritario']
      },
      cta: 'Comenzar Ahora',
      guarantee: 'Garantía de satisfacción de 30 días'
    },
    // Auth
    auth: {
      login: 'Iniciar Sesión',
      register: 'Crear Cuenta',
      email: 'Email',
      password: 'Contraseña',
      name: 'Nombre',
      loginWithGoogle: 'Continuar con Google',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      createAccount: 'Crear cuenta',
      loginHere: 'Inicia sesión aquí',
      forgotPassword: '¿Olvidaste tu contraseña?'
    },
    // Dashboard
    dashboard: {
      title: 'Tu Biblioteca de Audios',
      subtitle: '',
      categories: {
        all: 'Todos',
        sleep: 'Sueño',
        anxiety: 'Ansiedad',
        depression: 'Depresión',
        focus: 'Concentración',
        meditation: 'Meditación',
        binaural: 'Binaurales',
        nature: 'Naturaleza',
        other: 'Otros'
      },
      noSubscription: 'Necesitas una suscripción activa para acceder a los audios',
      subscribe: 'Ver Planes'
    },
    // Quiz
    quiz: {
      title: 'Evaluación de Salud Nerviosa',
      subtitle: 'Responde estas preguntas para obtener tu puntaje personalizado',
      scale: ['Nunca', 'Raramente', 'A veces', 'Frecuentemente', 'Siempre'],
      submit: 'Ver Resultados',
      result: {
        title: 'Tu Resultado',
        score: 'Puntuación',
        recommendation: 'Recomendación'
      }
    },
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Ha ocurrido un error',
      tryAgain: 'Intentar de nuevo',
      learnMore: 'Saber más',
      startNow: 'Comenzar Ahora',
      back: 'Volver'
    }
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      insomnia: 'Insomnia',
      anxiety: 'Anxiety',
      technology: 'Technology',
      pricing: 'Pricing',
      login: 'Login',
      register: 'Sign Up',
      dashboard: 'My Audios',
      logout: 'Logout',
      profile: 'Profile'
    },
    // Home page
    home: {
      title: 'BIOSOUNDS',
      subtitle: 'Applied Neuroacoustics - Music and Binaural Frequencies',
      description: 'BIOSOUNDS is an innovative applied neuroacoustics platform that uses 8D music and binaural frequencies to induce specific brain states through the Frequency Following Response (FFR) principle.',
      cta: 'Evaluate Your Nervous Health',
      ctaDescription: 'Get a personalized autonomic balance score in less than 5 minutes',
      benefits: {
        title: 'Scientifically Validated Benefits',
        items: [
          { title: 'Deep Sleep', desc: 'Delta wave induction for restorative sleep', stat: '+48% quality' },
          { title: 'Anxiety Reduction', desc: 'HPA axis and vagal tone modulation', stat: '-35% cortisol' },
          { title: 'HRV Improvement', desc: 'Optimized heart rate variability', stat: '+18% HRV' },
          { title: 'Anti-inflammatory', desc: 'Proinflammatory cytokine inhibition', stat: '-78% IL-6' }
        ]
      },
      science: {
        title: 'Backed by 50+ scientific studies (2020-2025)',
        items: [
          'Baseanu 2024 meta-analysis: 35% cortisol reduction',
          'Dabiri 2022: 48% sleep latency reduction',
          'Yang 2025: 18% HRV improvement',
          'Vanneste 2017: 78% reduction in inflammatory markers'
        ]
      }
    },
    // Insomnia page
    insomnia: {
      title: 'End Chronic Insomnia',
      subtitle: 'Restore Your Circadian Cycle with Validated Delta Waves',
      description: 'The 20-minute BIOSOUNDS Circadian Cycle (Delta) audio applies binaural frequencies to induce Delta (0.5–4 Hz) and Theta (4–8 Hz) waves, facilitating rapid transition to deep NREM sleep.',
      benefits: [
        'Precise Delta wave induction for deep restorative sleep',
        'Physical and psychological nighttime repair',
        'Elimination of night awakenings (up to 48% less)',
        'Restoration of disrupted circadian rhythm',
        'Reduction of nighttime inflammation'
      ],
      results: {
        title: 'Expected Results',
        items: [
          { week: 'Week 1', desc: 'More continuous sleep, initial reduction in interruptions' },
          { week: 'Week 2-4', desc: 'Greater sleep depth, significant HRV improvement' },
          { week: 'Month 2+', desc: 'Sustained daytime energy, stable autonomic balance' }
        ]
      },
      cta: 'Try Delta Audio'
    },
    // Anxiety page
    anxiety: {
      title: 'Control Anxiety and Cortisol',
      subtitle: 'Recover Your Natural Calm with Scientifically Validated Theta Waves',
      description: 'The 23-minute BIOSOUNDS Cortisol Control (Theta) audio uses Theta waves (4–8 Hz) to normalize stress metabolism, stabilize the HPA axis and reduce hyperactive emotional response.',
      benefits: [
        'Drastic reduction in circulating cortisol (average 35% reduction)',
        'Decrease in emotional and behavioral anxiety',
        'Greater mental clarity and emotional stability (+45%)',
        'Natural metabolic support for weight loss',
        'Increased vagal tone and HRV (+18%)'
      ],
      results: {
        title: 'Expected Results',
        items: [
          { week: 'Week 1', desc: 'Immediate calm sensation and reduction in anxiety spikes' },
          { week: 'Week 2-4', desc: 'Fewer episodes of generalized anxiety' },
          { week: 'Month 2+', desc: 'Sustained emotional balance, energy without burnout' }
        ]
      },
      cta: 'Try Theta Audio'
    },
    // Technology page
    technology: {
      title: 'The Technology Behind BIOSOUNDS',
      subtitle: 'Advances in Sound Neurobiomodulation and Auditory Neuromodulation',
      description: 'BIOSOUNDS integrates principles of biophysics, neuroscience and bioelectrics to offer cutting-edge non-invasive sound neuromodulation.',
      mechanisms: {
        title: 'Main Scientific Mechanisms',
        items: [
          { title: 'Frequency Following Response (FFR)', desc: 'Synchronization of neural oscillations with external frequencies' },
          { title: 'Cellular Bio-resonance', desc: 'Restores healthy vibrations in stressed cells' },
          { title: 'HPA Axis Modulation', desc: '18% HRV increase and inflammation reduction' },
          { title: 'Vagus Nerve Stimulation', desc: 'Auditory pathways activate parasympathetic reflex' }
        ]
      },
      evidence: {
        title: 'Scientific Evidence (2020-2025)',
        items: [
          'Anxiety reduction: 26-35% on standardized scales (Baseanu 2024)',
          'Sleep improvement: 48% reduced latency, +31% quality (Dabiri 2022)',
          'HRV increase: +18% after regular sessions (Yang 2025)',
          'Inflammatory reduction: cytokine inhibition (Vanneste 2017)',
          '0 serious adverse events reported in literature'
        ]
      }
    },
    // Pricing page
    pricing: {
      title: 'Choose Your Plan',
      subtitle: 'Unlimited access to all neuromodulation audios',
      monthly: {
        name: 'Monthly Plan',
        price: '€2.99',
        period: '/month',
        features: ['Access to all audios', 'Unlimited streaming', 'New monthly content', 'Email support']
      },
      annual: {
        name: 'Annual Plan',
        price: '€25.12',
        period: '/year',
        discount: 'Save 30%',
        features: ['Everything in monthly plan', 'Equivalent to €2.09/month', 'Priority access to new content', 'Priority support']
      },
      cta: 'Start Now',
      guarantee: '30-day satisfaction guarantee'
    },
    // Auth
    auth: {
      login: 'Login',
      register: 'Create Account',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      loginWithGoogle: 'Continue with Google',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      createAccount: 'Create account',
      loginHere: 'Login here',
      forgotPassword: 'Forgot your password?'
    },
    // Dashboard
    dashboard: {
      title: 'Your Audio Library',
      subtitle: '',
      categories: {
        all: 'All',
        sleep: 'Sleep',
        anxiety: 'Anxiety',
        depression: 'Depression',
        focus: 'Focus',
        meditation: 'Meditation',
        binaural: 'Binaural',
        nature: 'Nature',
        other: 'Other'
      },
      noSubscription: 'You need an active subscription to access audios',
      subscribe: 'View Plans'
    },
    // Quiz
    quiz: {
      title: 'Nervous Health Assessment',
      subtitle: 'Answer these questions to get your personalized score',
      scale: ['Never', 'Rarely', 'Sometimes', 'Frequently', 'Always'],
      submit: 'See Results',
      result: {
        title: 'Your Result',
        score: 'Score',
        recommendation: 'Recommendation'
      }
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      tryAgain: 'Try again',
      learnMore: 'Learn more',
      startNow: 'Start Now',
      back: 'Back'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('biosounds_language');
    return saved || 'es';
  });

  useEffect(() => {
    localStorage.setItem('biosounds_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
